<template>
  <div>
    <v-row>
      <v-col cols="6">
        <span class="text-h5">{{ $t('columnsList') }}</span>
        <v-chip class="ml-2 mb-1" color="primary">
          {{ columns.length ? columns.length - selectedColumns.length : '?' }}
        </v-chip>
        <v-text-field
          class="mb-6"
          :label="$t('search')"
          v-model="filterColumns"
          :loading="isLoading"
          clearable
        ></v-text-field>
        <v-list v-if="!isLoading" class="border-primary">
          <v-virtual-scroll
            :items="notSelectedColumns"
            height="316"
            item-height="48"
          >
            <template v-slot:default="{ item }">
              <v-list-item :key="item.value" @click="selectColumn(item.value)">
                <v-list-item-icon class="my-auto">
                  <v-icon> mdi-{{ getIcon(item.type) }} </v-icon>
                </v-list-item-icon>
                <v-list-item-title>
                  {{ item.text }}
                </v-list-item-title>
              </v-list-item>
            </template>
          </v-virtual-scroll>
        </v-list>
      </v-col>
      <v-col cols="6">
        <span class="text-h5">{{ $t('selectedColumns') }}</span>
        <v-chip
          class="ml-2 mb-1"
          :color="
            columns.length && !selectedColumns.length ? 'error' : 'primary'
          "
        >
          {{ isLoading ? '?' : selectedColumns.length }}
        </v-chip>
        <v-text-field
          class="mb-6"
          :label="$t('search')"
          v-model="filterSelectedColumns"
          :loading="isLoading"
          clearable
        ></v-text-field>
        <div
          :class="
            'border-primary' +
            (columns.length && !selectedColumns.length ? ' border-error' : '')
          "
          v-if="!isLoading"
        >
          <v-virtual-scroll
            :item-height="43.2 * selectedColumnsFiltered.length"
            height="316"
            :items="[1]"
          >
            <template v-slot:default>
              <draggable
                v-model="selectedColumnsFiltered"
                :disabled="filterSelectedColumns !== ''"
                ghost-class="ghost"
                @start="drag = true"
                @end="drag = false"
              >
                <v-list-item
                  v-for="column in selectedColumnsFiltered"
                  :key="column.value"
                  @click="deleteColumn(column.value)"
                >
                  <v-list-item-icon class="my-auto">
                    <v-icon>mdi-{{ getIcon(column.type) }}</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>
                    {{ column.text }}
                  </v-list-item-title>
                  <v-list-item-icon
                    class="my-3"
                    v-if="filterSelectedColumns === ''"
                  >
                    <v-icon>mdi-drag-horizontal-variant</v-icon>
                  </v-list-item-icon>
                </v-list-item>
              </draggable>
            </template>
          </v-virtual-scroll>
        </div>
      </v-col>
    </v-row>
    <v-row v-if="!isLoading">
      <v-col class="text-center" cols="6">
        <v-btn
          color="primary"
          @click="selectAllColumns"
          :disabled="selectedColumns.length == columns.length"
        >
          {{ $t('selectAll') }}
        </v-btn>
      </v-col>
      <v-col class="text-center" cols="6">
        <v-btn
          color="primary"
          @click="deselectAllColumns"
          :disabled="!selectedColumns.length"
        >
          {{ $t('deselectAll') }}
        </v-btn>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { getIcon } from '@/utils/supported-export-types.js'
import draggable from 'vuedraggable'

export default {
  name: 'ColumnsSelection',
  components: {
    draggable,
  },
  data() {
    return {
      drag: false,
      filterColumns: '',
      filterSelectedColumns: '',
    }
  },
  props: {
    columns: {
      type: Array,
      default: () => [],
    },
    selectedColumns: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    isLoading() {
      return !this.columns?.length
    },
    notSelectedColumns() {
      return this.columns.filter(({ text, value }) => {
        return (
          !this.selectedColumns.includes(value) &&
          text.includes(this.filterColumns ?? '')
        )
      })
    },
    selectedColumnsFiltered: {
      get() {
        return this.selectedColumns.flatMap((value) => {
          const column = this.columns.find((c) => c.value === value)
          if (!column?.text?.includes(this.filterSelectedColumns ?? ''))
            return []
          return [column]
        })
      },
      set(newValue) {
        const currentSelected = this.selectedColumns.flatMap((value) => {
          const column = this.columns.find((c) => c.value === value)
          return column ? [column] : []
        })

        const filteredIndexMap = new Map(
          this.selectedColumnsFiltered.map((col, index) => [col.text, index])
        )

        currentSelected.sort((a, b) => {
          const aFiltered = filteredIndexMap.has(a.text)
          const bFiltered = filteredIndexMap.has(b.text)

          if (!aFiltered && !bFiltered) return 0
          if (!aFiltered) return 1
          if (!bFiltered) return -1

          const aNewIndex = newValue.findIndex((col) => col.text === a.text)
          const bNewIndex = newValue.findIndex((col) => col.text === b.text)
          return aNewIndex - bNewIndex
        })

        this.selectedColumns.splice(
          0,
          this.selectedColumns.length,
          ...currentSelected.map((col) => col.value)
        )
      },
    },
  },
  methods: {
    getIcon,
    selectColumn(column_id) {
      this.selectedColumns.push(column_id)
      this.$emit('changeSelectedColumns', this.selectedColumns)
    },
    deleteColumn(column_id) {
      const index = this.selectedColumns.indexOf(column_id)
      if (index !== -1) {
        this.selectedColumns.splice(index, 1)
      }
      this.$emit('changeSelectedColumns', this.selectedColumns)
    },
    selectAllColumns() {
      for (const { value } of this.notSelectedColumns) {
        this.selectedColumns.push(value)
      }
      this.$emit('changeSelectedColumns', this.selectedColumns)
    },
    deselectAllColumns() {
      this.selectedColumns.splice(0)
      this.$emit('changeSelectedColumns', this.selectedColumns)
    },
  },
}
</script>

<style scoped>
.ghost {
  opacity: 0.5;
  background: lightgray;
}

.border-primary {
  border: 2px solid #2d62ed;
  border-radius: 8px;
  overflow: hidden;
  padding: 0;
  box-sizing: border-box;
}

.border-error {
  border-color: #dd6557;
}
</style>
